import { sendDataLayerEvent } from "@aistihealth/web-common/src/gtm/gtm-utils"
import { ErrorCode } from "@aistihealth/web-common/src/util/gql/error-codes"
import { emailRegex } from "@aistihealth/web-common/src/util/validation-utils"
import React, { useCallback, useMemo, useState } from "react"
import { GetServerSideProps, NextPage } from "next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { Formik, Form, FormikConfig } from "formik"
import { Row } from "react-styled-flexboxgrid"
import * as Yup from "yup"
import { Trans, useTranslation } from "next-i18next"
import { StructuredText } from "react-datocms"

import { initDatoClient } from "@aistihealth/web-common/src/datocms/gql/client/apollo-client"
import {
  GetStartTrialPageQuery,
  GetStartTrialPageDocument,
  GetStartTrialPageQueryVariables,
} from "@aistihealth/web-common/src/datocms/gql/generated/graphql"
import { localeToDatoLocale } from "@aistihealth/web-common/src/datocms/dato-utils"
import { Heading } from "@aistihealth/web-common/src/components/heading/Heading"
import { Paragraph } from "@aistihealth/web-common/src/components/paragraph/Paragraph"
import { Button } from "@aistihealth/web-common/src/components/button/Button"
import {
  omitParagraphTagRenderRule,
  paragraphRenderRule,
} from "@aistihealth/web-common/src/datocms/renderNodeRules"
import { SingleColLayout } from "@aistihealth/web-common/src/components/single-col-layout/SingleColLayout"
import { initApollo } from "../api/gql/client"
import { getErrorCode, hasErrorCode } from "../api/gql/error-utils"
import { useStartTrialMutation } from "../api/gql/public/generated/graphql"
import { i18nLanguageToGqlApiLanguage } from "../api/gql/public/language-util"
import { FormCol } from "../components/form/FormCol"
import { FormInput } from "../components/form/FormInput"
import { FormRow } from "../components/form/FormRow"
import { MainLayout } from "../components/layouts/main-layout/MainLayout"
import { Section } from "../components/section/Section"
import { ValidationErrorMessage } from "../components/form/ValidationErrorMessage"
import { testIds } from "../util/element-selectors"
import { defaultIfString } from "../util/typing/i18n"
import { trimObject } from "../util/user-input"
import { HeroBlock } from "../datocms/components/HeroBlock"
import { BasePageProps } from "../util/page"

const surveyBaseUrl = process.env.NEXT_PUBLIC_WS_EXT_APP_BASE_URL
const token = process.env.DATOCMS_API_TOKEN
const environment = process.env.DATOCMS_ENVIRONMENT

export const getServerSideProps: GetServerSideProps = async ({
  locale,
  preview,
}) => {
  const datoClient = initDatoClient({ token, environment, preview })
  const page = await datoClient.query<
    GetStartTrialPageQuery,
    GetStartTrialPageQueryVariables
  >({
    query: GetStartTrialPageDocument,
    variables: {
      locale: localeToDatoLocale(locale),
    },
  })
  return {
    props: {
      ...(await serverSideTranslations(locale, ["common", "start-trial"])),
      footer: page.data.marketingSitePageStructure.footer[0],
      page: page.data.startTrialPage,
      titleSuffix: page.data.site.globalSeo.titleSuffix,
    },
  }
}

const startTrialFormSchema = Yup.object({
  firstName: Yup.string()
    .required()
    .trim()
    .min(1)
    .max(70),
  lastName: Yup.string()
    .required()
    .trim()
    .min(1)
    .max(70),
  email: Yup.string()
    .required()
    .trim()
    .matches(emailRegex, "forms.validation.emailInvalid")
    .max(200),
  organization: Yup.string()
    .trim()
    .min(1)
    .max(70),
  phoneNumber: Yup.string()
    .trim()
    .matches(
      /^\+([0-9]| |-)+$/,
      "forms.validation.phoneNumberShouldBeInIntlFormat",
    )
    .min(7)
    .max(31),
})

type StartTrialFormFields = Yup.Asserts<typeof startTrialFormSchema>
type StartTrialFormSubmitFn = FormikConfig<StartTrialFormFields>["onSubmit"]

const initialValues: StartTrialFormFields = {
  firstName: "",
  lastName: "",
  email: "",
  organization: "",
  phoneNumber: "",
}

const formId = "startTrial"

type StartTrialProps = {
  page: GetStartTrialPageQuery["startTrialPage"]
  titleSuffix: string
} & BasePageProps

const StartTrial: NextPage<StartTrialProps> = ({
  page,
  titleSuffix,
  footer,
}) => {
  const { t, i18n } = useTranslation()

  const [loading, setLoading] = useState(false)
  const [complete, setComplete] = useState(false)
  const [successEmail, setSuccessEmail] = useState("")
  const [submitError, setSubmitError] = useState("")
  const apolloClient = useMemo(() => {
    return initApollo("public")
  }, [])

  const heroModel = page.hero[0]
  const hero = heroModel && <HeroBlock model={heroModel} />
  const intro = page.intro[0]
  const form = page.form[0]

  const introRenderNodeRules = useMemo(() => {
    return [paragraphRenderRule({ size: "large" })]
  }, [])

  const formRenderNodeRules = useMemo(() => {
    return [paragraphRenderRule({ size: "large" })]
  }, [])

  const [startTrial] = useStartTrialMutation({ client: apolloClient })

  const handleSubmit: StartTrialFormSubmitFn = useCallback(
    async values => {
      setLoading(true)
      setSubmitError("")

      const {
        email,
        firstName,
        lastName,
        organization,
        phoneNumber,
      } = trimObject(values)

      try {
        await startTrial({
          variables: {
            input: {
              email,
              firstName,
              lastName,
              organization: organization || null,
              phoneNumber: phoneNumber || null,
              language: i18nLanguageToGqlApiLanguage(i18n.language),
            },
          },
        })
        sendDataLayerEvent({
          event: "startTrial",
        })
        setComplete(true)
        setSuccessEmail(email)
      } catch (error) {
        if (
          hasErrorCode(error, [
            ErrorCode.USER_ALREADY_EXISTS,
            ErrorCode.PHONE_NUMBER_ALREADY_TAKEN,
          ])
        ) {
          setSubmitError(
            t(`start-trial:errors.${getErrorCode(error)}`, { surveyBaseUrl }),
          )
        } else {
          setSubmitError(t("start-trial:form.submitError"))
          throw error
        }
      } finally {
        setLoading(false)
      }
    },
    [i18n.language, startTrial, t],
  )

  const fieldLabels: Record<string, string> = defaultIfString(
    t("common:forms.fieldLabels", {
      returnObjects: true,
    }),
    {},
  )

  const mainContent = (
    <>
      <Heading as="h2" size="xlarge" align="center">
        <StructuredText
          data={intro.header.value}
          customNodeRules={[omitParagraphTagRenderRule]}
        />
      </Heading>
      <Row>
        <StructuredText
          data={intro.text.value}
          customNodeRules={introRenderNodeRules}
        />
      </Row>
      <Heading size="large">
        <StructuredText
          data={form.header.value}
          customNodeRules={[omitParagraphTagRenderRule]}
        />
      </Heading>
      <Formik<StartTrialFormFields>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={startTrialFormSchema}
      >
        {({ isValid }) => {
          return (
            <Form noValidate>
              <FormRow>
                <FormCol xs={12} md={6}>
                  <FormInput
                    type="text"
                    formId={formId}
                    name="firstName"
                    displayName={fieldLabels.firstName ?? "firstName"}
                    required
                  />
                </FormCol>
                <FormCol xs={12} md={6}>
                  <FormInput
                    type="text"
                    formId={formId}
                    name="lastName"
                    displayName={fieldLabels.lastName ?? "lastName"}
                    required
                  />
                </FormCol>
              </FormRow>
              <FormRow>
                <FormCol xs={12} md={6}>
                  <FormInput
                    type="text"
                    formId={formId}
                    name="email"
                    displayName={fieldLabels.email ?? "email"}
                    required
                  />
                </FormCol>
                <FormCol xs={12} md={6}>
                  <FormInput
                    type="text"
                    formId={formId}
                    name="phoneNumber"
                    displayName={fieldLabels.phoneNumber ?? "phoneNumber"}
                  />
                </FormCol>
              </FormRow>
              <FormRow>
                <FormCol xs={12} md={6}>
                  <FormInput
                    type="text"
                    formId={formId}
                    name="organization"
                    displayName={fieldLabels.organization ?? "organization"}
                  />
                </FormCol>
              </FormRow>
              <FormRow>
                <FormCol xs={12}>
                  {submitError && (
                    <ValidationErrorMessage as="div" size="large">
                      <Trans
                        i18nKey={submitError}
                        components={{
                          // eslint-disable-next-line jsx-a11y/anchor-has-content
                          a: <a />,
                        }}
                      />
                    </ValidationErrorMessage>
                  )}
                </FormCol>
              </FormRow>
              <FormRow>
                <StructuredText
                  data={form.text.value}
                  customNodeRules={formRenderNodeRules}
                />
              </FormRow>
              <FormRow align="right">
                <FormCol xs={12}>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={loading || !isValid}
                  >
                    {t("start-trial:form.submit")}
                  </Button>
                </FormCol>
              </FormRow>
            </Form>
          )
        }}
      </Formik>
    </>
  )

  const postSubmitContent = (
    <>
      <Heading size="xlarge" data-test-id={testIds.startTrial.successHeader}>
        {t("start-trial:postSubmit.title")}
      </Heading>
      <Paragraph size="large" data-test-id={testIds.startTrial.successMessage}>
        {t("start-trial:postSubmit.body", {
          email: successEmail,
        })}
      </Paragraph>
    </>
  )

  return (
    <MainLayout
      type="metaTags"
      metaTags={page.seoMetaTags}
      titleSuffix={titleSuffix}
      footer={footer}
      heroComponent={hero}
    >
      <Section backgroundColor="white">
        <SingleColLayout
          colProps={{
            sm: 8,
            smOffset: 2,
            xs: 12,
          }}
        >
          {!complete && mainContent}
          {complete && postSubmitContent}
        </SingleColLayout>
      </Section>
    </MainLayout>
  )
}

export default StartTrial
